import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Shield, ShieldCheck, Search } from 'lucide-react';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { UserAvatar } from '@/components/shared/UserAvatar';
import { useToast } from '@/hooks/use-toast';
import { sprintService, type SprintDetail } from '@/services/sprint.service';
import { groupService } from '@/services/group.service';
import { cn } from '@/lib/utils';

interface SprintTeamDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sprintId: string;
  groupId: string;
  members: SprintDetail['members'];
  canManage: boolean;
}

export function SprintTeamDialog({ open, onOpenChange, sprintId, groupId, members, canManage }: SprintTeamDialogProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [search, setSearch] = useState('');

  // Fetch group members to show available users
  const { data: groupMembers } = useQuery({
    queryKey: ['group-members', groupId],
    queryFn: () => groupService.listMembers(groupId).then((r) => r.data.data),
    enabled: !!groupId && open,
  });

  const memberMap = new Map(members.map((m) => [m.user?.id, m]));
  const managerCount = members.filter((m) => m.role === 'manager' || m.role === 'owner').length;

  const addMut = useMutation({
    mutationFn: (userId: string) => sprintService.addMember(sprintId, { user_id: userId, role: 'member' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sprint', sprintId] });
      toast({ title: 'Member added' });
    },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Failed', variant: 'destructive' }),
  });

  const removeMut = useMutation({
    mutationFn: (memberId: string) => sprintService.removeMember(sprintId, memberId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sprint', sprintId] });
      toast({ title: 'Member removed' });
    },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Failed', variant: 'destructive' }),
  });

  const toggleRoleMut = useMutation({
    mutationFn: ({ memberId, role }: { memberId: string; role: string }) =>
      sprintService.updateMemberRole(sprintId, memberId, role),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sprint', sprintId] });
    },
    onError: (e: any) => toast({ title: 'Error', description: e.response?.data?.error?.message || 'Failed', variant: 'destructive' }),
  });

  function toggleMember(userId: string) {
    if (!canManage) return;
    const existing = memberMap.get(userId);
    if (existing) {
      if (existing.role === 'owner') return;
      removeMut.mutate(existing.id);
    } else {
      addMut.mutate(userId);
    }
  }

  function toggleManager(memberId: string, currentRole: string) {
    if (!canManage) return;
    if (currentRole === 'owner') return;
    const newRole = currentRole === 'manager' ? 'member' : 'manager';
    toggleRoleMut.mutate({ memberId, role: newRole });
  }

  const allGroupMembers = (groupMembers ?? []).filter((gm: any) => {
    const name = gm.user?.full_name || '';
    return name.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[550px] max-h-[85vh]">
        <DialogHeader>
          <DialogTitle>Sprint Team & Managers</DialogTitle>
          <p className="text-sm text-muted-foreground">
            {canManage
              ? 'Select group members for this sprint. Toggle the shield icon to make them managers who can edit tasks.'
              : 'View sprint team members and their roles.'}
          </p>
        </DialogHeader>

        <div className="relative mb-2">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search members..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" />
        </div>

        <ScrollArea className="max-h-[50vh]">
          <div className="space-y-1 pr-2">
            {allGroupMembers.map((gm: any) => {
              const uid = gm.user?.id || gm.user_id;
              const name = gm.user?.full_name || 'Unknown';
              const avatar = gm.user?.avatar_url;
              const sprintMember = memberMap.get(uid);
              const isChecked = !!sprintMember;
              const isOwner = sprintMember?.role === 'owner';
              const isManager = sprintMember?.role === 'manager' || isOwner;

              return (
                <div
                  key={uid}
                  className={cn(
                    'flex items-center gap-3 p-2.5 rounded-lg transition-colors',
                    isChecked && 'bg-muted/50',
                    canManage && 'cursor-pointer hover:bg-muted/50'
                  )}
                >
                  {canManage && (
                    <Checkbox
                      checked={isChecked}
                      disabled={isOwner}
                      onCheckedChange={() => toggleMember(uid)}
                    />
                  )}
                  <UserAvatar name={name} avatarUrl={avatar} size="sm" />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{name}</p>
                  </div>
                  {isChecked && (
                    <div className="flex items-center gap-2">
                      <Badge variant={isManager ? 'default' : 'secondary'} className="text-[10px]">
                        {isOwner ? 'Owner' : isManager ? 'Manager' : 'Team Member'}
                      </Badge>
                      {canManage && !isOwner && (
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); toggleManager(sprintMember!.id, sprintMember!.role); }}
                          className={cn(
                            'rounded p-1 transition-colors',
                            isManager ? 'text-brand-primary bg-brand-primary/10' : 'text-muted-foreground hover:text-brand-primary'
                          )}
                          title={isManager ? 'Remove manager role' : 'Make manager'}
                        >
                          {isManager ? <ShieldCheck className="h-4 w-4" /> : <Shield className="h-4 w-4" />}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </ScrollArea>

        <DialogFooter className="flex items-center justify-between sm:justify-between">
          <span className="text-xs text-muted-foreground">
            {members.length} members &middot; {managerCount} managers
          </span>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
